/**
 * NavBar — the authenticated app header: brand, role-aware navigation
 * (student / teacher / admin / guardian), the two-cluster student nav
 * (Academics | Career), the Settings gear, and the mobile account control.
 *
 * Extracted from App.js (shell split) — BYTE-IDENTICAL body. Rendered by
 * AuthenticatedApp with props { user, onLogout, onUpdateUser }.
 *
 * The `_prefetch*` route-chunk warmers live in the `../routePrefetch` leaf
 * module (imported here for hover/focus warming, and by App.handleLogin for
 * post-auth warming) — a shared leaf, so there is no App↔NavBar import cycle.
 * SettingsGearIcon lives here as NavBar's own dependency.
 */
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router';
import LiangFanLogo from './ui/LiangFanLogo';
// Issue #56 (2026-06-10): per-user Settings dialog (hide Admissions/Career
// nav sections). Eager-imported (not lazy) because it's small and we want the
// gear → open interaction to feel instant; it renders nothing until opened.
import SettingsDialog from '../SettingsDialog';
import {
  _prefetchChat,
  _prefetchProgress,
  _prefetchLearningPaths,
  _prefetchExerciseLayout,
  _prefetchClassrooms,
  _prefetchPrep,
  _prefetchAdvisor,
  _prefetchCareer,
  _prefetchCareerSignoff,
  _prefetchGuardianHome,
  _prefetchFounderDashboard,
} from '../routePrefetch';

// Shared settings-gear glyph. Extracted (audit r3) so the desktop `.nav-user`
// gear and the mobile account control render the identical icon from one source
// rather than duplicating the SVG path.
function SettingsGearIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
        stroke="currentColor"
        strokeWidth="1.7"
      />
      <path
        d="M19.4 13a7.6 7.6 0 0 0 .05-2l1.7-1.32a.5.5 0 0 0 .12-.64l-1.6-2.77a.5.5 0 0 0-.6-.22l-2 .8a7.4 7.4 0 0 0-1.74-1l-.3-2.13a.5.5 0 0 0-.5-.42h-3.2a.5.5 0 0 0-.5.42l-.3 2.12a7.5 7.5 0 0 0-1.74 1.01l-2-.8a.5.5 0 0 0-.6.22L2.78 9.04a.5.5 0 0 0 .12.64L4.6 11a7.6 7.6 0 0 0 0 2l-1.7 1.32a.5.5 0 0 0-.12.64l1.6 2.77a.5.5 0 0 0 .6.22l2-.8a7.4 7.4 0 0 0 1.74 1l.3 2.13a.5.5 0 0 0 .5.42h3.2a.5.5 0 0 0 .5-.42l.3-2.12a7.5 7.5 0 0 0 1.74-1.01l2 .8a.5.5 0 0 0 .6-.22l1.6-2.77a.5.5 0 0 0-.12-.64L19.4 13Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function NavBar({ user, onLogout, onUpdateUser }) {
  const location = useLocation();
  const isTeacher = user?.is_teacher === true;
  // POST-AIQ-122: is_admin is set on the user payload at login/register
  // time, derived from ADMIN_EMAILS server-side. Frontend gating is for
  // UX only — the /admin/prep API enforces the same gate.
  const isAdmin = user?.is_admin === true;
  // Task #47 GE-PR4: is_guardian is the guardian ROLE flag (set at
  // registration, auth.py `req.role == "guardian"`), shipped on the
  // login/refresh payload — NOT derived from holding a guardian link. It gates
  // the /family entry — shown for guardians in BOTH the teacher and student nav
  // blocks (a guardian may also be a teacher or a student). UX gate only — the
  // guardian routes + the verified-link/consent gates enforce server-side.
  const isGuardian = user?.is_guardian === true;
  // Issue #56: per-user hide-section UX preference. A missing/partial value
  // reads as "everything visible" (the backend normalizes to a full object,
  // but we stay defensive for older `user` payloads). DISPLAY-ONLY — never an
  // access signal; the consent gates remain authoritative.
  const hidden = user?.hidden_sections || {};
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <header className="App-header" role="banner">
      <div className="header-content">
        <Link to={isTeacher ? '/classrooms' : '/learn'} className="header-brand" aria-label="Liang — home">
          <LiangFanLogo variant="mark" size={40} title="" className="header-brand__mark" />
          <span className="header-brand__text">
            <span className="liang-wordmark liang-wordmark--header">Liang</span>
            <span className="liang-overline liang-overline--inverse header-brand__tagline">
              {isTeacher ? 'Manage your classrooms' : 'Learn through guided discovery'}
            </span>
          </span>
        </Link>
        <nav className="header-nav" aria-label="Main navigation">
          {isTeacher ? (
            <>
              <Link
                to="/classrooms"
                className={`nav-link ${location.pathname.startsWith('/classrooms') ? 'active' : ''}`}
                aria-current={location.pathname.startsWith('/classrooms') ? 'page' : undefined}
                onMouseEnter={_prefetchClassrooms}
                onFocus={_prefetchClassrooms}
              >
                Classrooms
              </Link>
              <Link
                to="/dashboard"
                className={`nav-link ${location.pathname === '/dashboard' ? 'active' : ''}`}
                aria-current={location.pathname === '/dashboard' ? 'page' : undefined}
              >
                Dashboard
              </Link>
              {/* POST-AIQ-151 COPPA-PR4: teacher Career sign-off queue. The
                  Career composite gate needs a teacher's approval for every
                  minor; this is where teachers act on those requests. */}
              <Link
                to="/career-signoff"
                className={`nav-link ${location.pathname.startsWith('/career-signoff') ? 'active' : ''}`}
                aria-current={location.pathname.startsWith('/career-signoff') ? 'page' : undefined}
                onMouseEnter={_prefetchCareerSignoff}
                onFocus={_prefetchCareerSignoff}
              >
                Career sign-off
              </Link>
            </>
          ) : (
            <>
              {/* Issue #66: the student learning nav is grouped into two visual
                  CLUSTERS — Academics and Career — separated by a divider. Each
                  cluster is a real ARIA `group` with an `aria-label`, so the
                  grouping is announced to assistive tech (the cluster name is
                  the group's accessible name; no always-on visible label, which
                  would crowd the dense green header — divider + spacing carry it
                  visually). The Career cluster (its divider + group) renders
                  ONLY when Career is visible, so hiding it collapses cleanly
                  with no dangling divider and no empty group. Academics is
                  always non-empty (Learn/Paths/Prep/Progress are never
                  hideable). */}
              <div className="nav-group nav-group--academics" role="group" aria-label="Academics">
                <Link
                  to="/learn"
                  className={`nav-link ${location.pathname === '/learn' ? 'active' : ''}`}
                  aria-current={location.pathname === '/learn' ? 'page' : undefined}
                  onMouseEnter={_prefetchChat}
                  onFocus={_prefetchChat}
                >
                  Learn
                </Link>
                <Link
                  to="/paths"
                  className={`nav-link ${location.pathname === '/paths' ? 'active' : ''}`}
                  aria-current={location.pathname === '/paths' ? 'page' : undefined}
                  onMouseEnter={() => { _prefetchLearningPaths(); _prefetchExerciseLayout(); }}
                  onFocus={() => { _prefetchLearningPaths(); _prefetchExerciseLayout(); }}
                >
                  Paths
                </Link>
                <Link
                  to="/prep"
                  className={`nav-link ${location.pathname.startsWith('/prep') ? 'active' : ''}`}
                  aria-current={location.pathname.startsWith('/prep') ? 'page' : undefined}
                  onMouseEnter={_prefetchPrep}
                  onFocus={_prefetchPrep}
                >
                  Prep
                </Link>
                {/* POST-AIQ-143 PR 7: Admissions Advisor. Admissions is an
                    academic surface, so it lives in the Academics cluster.
                    Shown for guardians (who set up access) OR students (who
                    reach the consent CTA / their consented surfaces). UX gate
                    only — the backend + in-component consent gates enforce.
                    Issue #56: also suppressed when the user has hidden it via
                    Settings (a DISPLAY preference, not an access gate). */}
                {!hidden.admissions && (
                  <Link
                    to="/advisor"
                    className={`nav-link ${location.pathname.startsWith('/advisor') ? 'active' : ''}`}
                    aria-current={location.pathname.startsWith('/advisor') ? 'page' : undefined}
                    onMouseEnter={_prefetchAdvisor}
                    onFocus={_prefetchAdvisor}
                  >
                    Advisor
                  </Link>
                )}
                <Link
                  to="/progress"
                  className={`nav-link ${location.pathname === '/progress' ? 'active' : ''}`}
                  aria-current={location.pathname === '/progress' ? 'page' : undefined}
                  onMouseEnter={_prefetchProgress}
                  onFocus={_prefetchProgress}
                >
                  Progress
                </Link>
              </div>
              {/* POST-AIQ-150 CD-PR7: Career Discovery — its own cluster.
                  Self-serve for students (consent CTA + their consented
                  surfaces); the under-13 path falls back to guardian grant. UX
                  gate only — the backend + in-component consent gates enforce.
                  Issue #56: suppressed when the user has hidden it via Settings
                  (a DISPLAY preference, not an access gate) — and because the
                  divider lives INSIDE this conditional, hiding Career removes
                  the divider too (no dangling separator). */}
              {!hidden.career && (
                <>
                  {/* Purely visual cluster separator. The REAL grouping
                      semantics live on the two role="group" + aria-label
                      wrappers, so this divider is aria-hidden to keep the a11y
                      tree clean (no orphan separator node conveying nothing). */}
                  <span className="nav-cluster-divider" aria-hidden="true" />
                  <div className="nav-group nav-group--career" role="group" aria-label="Career">
                    <Link
                      to="/career"
                      className={`nav-link ${location.pathname.startsWith('/career') ? 'active' : ''}`}
                      aria-current={location.pathname.startsWith('/career') ? 'page' : undefined}
                      onMouseEnter={_prefetchCareer}
                      onFocus={_prefetchCareer}
                    >
                      Career
                    </Link>
                  </div>
                </>
              )}
            </>
          )}
          {/* Task #47 GE-PR4: the guardian Family home. Shown for any guardian
              (teacher or student account) — it's where a parent sees a child's
              growth summary, admissions, and access toggles. Mirrors the teacher
              Career-sign-off link's lazy-chunk + hover-prefetch conventions. */}
          {isGuardian && (
            <Link
              to="/family"
              className={`nav-link ${location.pathname.startsWith('/family') ? 'active' : ''}`}
              aria-current={location.pathname.startsWith('/family') ? 'page' : undefined}
              onMouseEnter={_prefetchGuardianHome}
              onFocus={_prefetchGuardianHome}
            >
              Family
            </Link>
          )}
          {isAdmin && (
            <>
              <Link
                to="/admin/prep"
                className={`nav-link ${location.pathname.startsWith('/admin/prep') ? 'active' : ''}`}
                aria-current={location.pathname.startsWith('/admin/prep') ? 'page' : undefined}
              >
                Admin
              </Link>
              {/* POST-AIQ-134 PR 5: admin-only analytics across all classrooms */}
              <Link
                to="/admin/dashboard"
                className={`nav-link ${location.pathname === '/admin/dashboard' ? 'active' : ''}`}
                aria-current={location.pathname === '/admin/dashboard' ? 'page' : undefined}
              >
                Admin Dashboard
              </Link>
              {/* ANL-PR4: the founder KPI dashboard (platform-wide, admin-only).
                  Hover/focus prefetch the chunk so the click feels instant. */}
              <Link
                to="/admin/kpis"
                className={`nav-link ${location.pathname === '/admin/kpis' ? 'active' : ''}`}
                aria-current={location.pathname === '/admin/kpis' ? 'page' : undefined}
                onMouseEnter={_prefetchFounderDashboard}
                onFocus={_prefetchFounderDashboard}
              >
                Founder KPIs
              </Link>
            </>
          )}
          <div className="nav-user">
            {/* Issue #56: Settings (hide Admissions/Career sections). Only
                non-teacher users have those sections, so the gear is theirs
                only. Surgical addition to the user area — kept to a single
                button so it doesn't collide with parallel edits here. */}
            {!isTeacher && (
              <button
                type="button"
                className="nav-settings"
                onClick={() => setSettingsOpen(true)}
                aria-label="Settings"
                aria-haspopup="dialog"
              >
                <SettingsGearIcon />
              </button>
            )}
            <span className="nav-username">{user?.username}</span>
            <button className="nav-logout" onClick={onLogout} aria-label="Log out">Logout</button>
          </div>
        </nav>
        {/* Mobile account control (audit r3). The desktop `.nav-user` above is
            `display:none` at ≤768px (it would otherwise wreck the fixed bottom
            tab bar), which left phone/tablet users with NO way to log out — and
            the shared-device PII wipe in handleLogout therefore unreachable.
            This compact control is the inverse: hidden on desktop, shown at
            ≤768px, pinned to the header's top-right (out of the bottom bar). It
            carries the same Settings gear (non-teachers) + a Logout icon-button
            (everyone), each a real <button> with an aria-label, touch-sized. */}
        <div className="nav-account-mobile">
          {!isTeacher && (
            <button
              type="button"
              className="nav-account-mobile__btn nav-settings-mobile"
              onClick={() => setSettingsOpen(true)}
              aria-label="Settings"
              aria-haspopup="dialog"
            >
              <SettingsGearIcon />
            </button>
          )}
          <button
            type="button"
            className="nav-account-mobile__btn nav-logout-mobile"
            onClick={onLogout}
            aria-label="Log out"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
              <path
                d="M15 17v1.5A2.5 2.5 0 0 1 12.5 21H6.5A2.5 2.5 0 0 1 4 18.5v-13A2.5 2.5 0 0 1 6.5 3h6A2.5 2.5 0 0 1 15 5.5V7"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10 12h10m0 0-3-3m3 3-3 3"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
      {/* Issue #56: the Settings dialog. Renders only while open; lives here so
          the gear's opener is restored on close (useRestoreFocus). */}
      {settingsOpen && (
        <SettingsDialog
          user={user}
          onUpdateUser={onUpdateUser}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </header>
  );
}

export default NavBar;
